import React, { useState, useRef } from 'react';
import { Modal, Form, Button, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../../config/api';
import uploadService from '../../../services/UploadService';
import styles from './ActionModals.module.scss';

/**
 * 💰 MARK AS PAID MODAL - Modal para Marcar Venda como Paga
 * 
 * @param {boolean} show - Controla visibilidade
 * @param {Function} onHide - Callback ao fechar
 * @param {number} saleId - ID da venda
 * @param {Object} sale - Dados da venda
 * @param {Function} onSuccess - Callback após sucesso
 */
const MarkAsPaidModal = ({ show, onHide, saleId, sale, onSuccess }) => {
  const [formData, setFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: sale?.payment_method || 'pix',
    receipt_url: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const fileInputRef = useRef(null);

  const paymentMethods = [
    { value: 'pix', label: 'PIX', icon: 'mdi:qrcode' },
    { value: 'credit_card', label: 'Cartão de Crédito', icon: 'mdi:credit-card' },
    { value: 'boleto', label: 'Boleto', icon: 'mdi:barcode' },
    { value: 'bank_transfer', label: 'Transferência Bancária', icon: 'mdi:bank-transfer' },
    { value: 'other', label: 'Outro', icon: 'mdi:cash' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  /**
   * 🖼️ Lidar com seleção de imagem
   */
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');

    try {
      // Validar arquivo
      const validation = uploadService.validateFile(file, {
        type: 'image',
        allowedFormats: uploadService.validImageFormats
      });

      if (!validation.valid) {
        throw new Error(validation.error);
      }

      setImageFile(file);

      // Criar preview local
      const preview = await uploadService.createPreview(file);
      setImagePreview(preview);

      // Fazer upload automaticamente
      await handleUploadImage(file);

    } catch (err) {
      console.error('❌ Erro ao selecionar imagem:', err);
      setError(err.message);
      setImageFile(null);
      setImagePreview(null);
    }
  };

  /**
   * 📤 Fazer upload da imagem
   */
  const handleUploadImage = async (file) => {
    if (!file) return;

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      // Upload
      const result = await uploadService.uploadImage(file, {
        onProgress: (progress) => setUploadProgress(progress)
      });

      setUploadProgress(100);

      // Salvar URL no formData
      setFormData(prev => ({
        ...prev,
        receipt_url: result.url
      }));

      console.log('✅ Comprovante enviado:', result.url);

    } catch (err) {
      console.error('❌ Erro no upload:', err);
      setError(err.message || 'Erro ao enviar comprovante');
      setImageFile(null);
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  /**
   * 🗑️ Remover imagem
   */
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      receipt_url: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sales/${saleId}/mark-as-paid`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao marcar como pago');
      }

      console.log('✅ Venda marcada como paga');

      // Reset e fechar
      setFormData({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'pix',
        receipt_url: '',
        notes: ''
      });
      onHide();
      
      if (onSuccess) {
        onSuccess();
      }

    } catch (err) {
      console.error('❌ Erro ao marcar como pago:', err);
      setError(err.message || 'Erro ao marcar venda como paga');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="md"
      backdrop={false}
      className={styles.actionModal}
      enforceFocus={false}
    >
      <Modal.Header closeButton className={styles.headerPaid}>
        <Modal.Title as="span" className={styles.modalTitle}>
          <Icon icon="mdi:cash-check" />
          Marcar como Pago
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className={styles.modalBody}>
          {error && (
            <Alert variant="danger" className="mb-3">
              <Icon icon="mdi:alert-circle" className="me-2" />
              {error}
            </Alert>
          )}

          {/* Data do Pagamento */}
          <Form.Group className="mb-3">
            <Form.Label>
              <Icon icon="mdi:calendar" className="me-2" />
              Data do Pagamento *
            </Form.Label>
            <Form.Control
              type="date"
              value={formData.payment_date}
              onChange={(e) => handleChange('payment_date', e.target.value)}
              disabled={loading}
              required
            />
          </Form.Group>

          {/* Método de Pagamento */}
          <Form.Group className="mb-3">
            <Form.Label>
              <Icon icon="mdi:credit-card-outline" className="me-2" />
              Método de Pagamento *
            </Form.Label>
            <Form.Select
              value={formData.payment_method}
              onChange={(e) => handleChange('payment_method', e.target.value)}
              disabled={loading}
              required
            >
              {paymentMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Upload do Comprovante */}
          <Form.Group className="mb-3">
            <Form.Label>
              <Icon icon="mdi:file-image" className="me-2" />
              Comprovante (opcional)
            </Form.Label>
            
            {!imagePreview ? (
              <>
                <div 
                  className="border-2 border-dashed rounded p-4 text-center"
                  style={{ borderColor: '#dee2e6', cursor: 'pointer' }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icon 
                    icon="mdi:cloud-upload" 
                    style={{ fontSize: '3rem', color: '#6b7280' }}
                  />
                  <p className="mb-0 mt-2" style={{ color: '#6b7280' }}>
                    Clique para selecionar ou arraste o comprovante
                  </p>
                  <small className="text-muted">
                    JPG, PNG ou PDF até 5MB
                  </small>
                </div>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  disabled={loading || uploading}
                  style={{ display: 'none' }}
                />
              </>
            ) : (
              <div className="position-relative">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="img-fluid rounded"
                  style={{ maxHeight: '200px', width: '100%', objectFit: 'contain', background: '#f8f9fa' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-2"
                  onClick={handleRemoveImage}
                  disabled={loading}
                >
                  <Icon icon="mdi:delete" />
                </Button>
                {uploading && (
                  <div className="mt-2">
                    <ProgressBar 
                      now={uploadProgress} 
                      label={`${uploadProgress}%`}
                      animated={uploadProgress < 100}
                    />
                  </div>
                )}
                {formData.receipt_url && !uploading && (
                  <div className="mt-2 text-success">
                    <Icon icon="mdi:check-circle" className="me-1" />
                    Comprovante enviado com sucesso!
                  </div>
                )}
              </div>
            )}
          </Form.Group>

          {/* Notas */}
          <Form.Group>
            <Form.Label>
              <Icon icon="mdi:note-text" className="me-2" />
              Observações (opcional)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Adicione informações adicionais sobre o pagamento..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={loading}
          >
            <Icon icon="mdi:close" className="me-2" />
            Cancelar
          </Button>
          
          <Button
            variant="success"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Salvando...
              </>
            ) : (
              <>
                <Icon icon="mdi:check-circle" className="me-2" />
                Confirmar Pagamento
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default MarkAsPaidModal;

